import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/ui';
import { cn } from '@/components/ui';
import {
  categoryDisplayName,
  useAdminCategoryItemsQuery,
  useCategoriesQuery,
  type CategoryItem,
} from '../hooks/useCategoriesQuery';

export interface CategorySearchSelectProps {
  value: string | null;
  disabled?: boolean;
  errorMessage?: string | null;
  onChange: (categoryId: string | null) => void;
  placeholder?: string;
  testId?: string;
  /**
   * 숨김(비활성) 카테고리까지 고를 수 있게 관리자 목록을 읽는다(상품 등록·수정 화면). 기본 false = 공개 목록
   * (상품 목록 필터·일괄 변경은 지금처럼). 숨김 카테고리는 "(숨김)" 이 붙는다.
   */
  includeHidden?: boolean;
}

interface CategoryNode extends CategoryItem {
  children: ReadonlyArray<CategoryNode>;
}

const buildTree = (items: ReadonlyArray<CategoryItem>): ReadonlyArray<CategoryNode> => {
  const childrenByParent = items.reduce<Map<string | null, CategoryItem[]>>((acc, item) => {
    const siblings = acc.get(item.parentCategoryId) ?? [];
    acc.set(item.parentCategoryId, [...siblings, item]);
    return acc;
  }, new Map());

  const attach = (parentId: string | null): ReadonlyArray<CategoryNode> =>
    (childrenByParent.get(parentId) ?? []).map((item) => ({
      ...item,
      children: attach(item.categoryId),
    }));

  return attach(null);
};

const buildPath = (
  items: ReadonlyArray<CategoryItem>,
  categoryId: string | null,
): ReadonlyArray<string> => {
  if (!categoryId) return [];
  const byId = new Map(items.map((c) => [c.categoryId, c]));
  const walk = (id: string | null, acc: ReadonlyArray<string>): ReadonlyArray<string> => {
    if (!id) return acc;
    const node = byId.get(id);
    if (!node) return acc;
    return walk(node.parentCategoryId, [categoryDisplayName(node), ...acc]);
  };
  return walk(categoryId, []);
};

const matchesKeyword = (node: CategoryNode, keyword: string): boolean => {
  const self =
    node.categoryName.toLowerCase().includes(keyword) ||
    node.categoryCode.toLowerCase().includes(keyword);
  return self || node.children.some((child) => matchesKeyword(child, keyword));
};

interface TreeRowProps {
  node: CategoryNode;
  depth: number;
  keyword: string;
  selectedId: string | null;
  onSelect: (categoryId: string) => void;
}

const TreeRow = ({ node, depth, keyword, selectedId, onSelect }: TreeRowProps) => {
  const hasChildren = node.children.length > 0;
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (keyword) setExpanded(true);
  }, [keyword]);

  const visibleChildren = keyword
    ? node.children.filter((child) => matchesKeyword(child, keyword))
    : node.children;

  const selected = node.categoryId === selectedId;

  return (
    <li>
      <div
        className={cn(
          'group flex items-center gap-1 rounded-md py-1.5 pr-2 transition-colors',
          'hover:bg-[color:var(--surface-1)]',
          selected && 'bg-[color:var(--surface-1)]',
        )}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded"
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          aria-label={expanded ? '하위 카테고리 접기' : '하위 카테고리 펼치기'}
        >
          {expanded ? (
            <ChevronDown size={14} style={{ color: 'var(--content-muted)' }} />
          ) : (
            <ChevronRight size={14} style={{ color: 'var(--content-muted)' }} />
          )}
        </button>
        <button
          type="button"
          onClick={() => onSelect(node.categoryId)}
          aria-pressed={selected}
          className="flex min-w-0 flex-1 items-center gap-2 py-0.5 text-left text-sm focus-visible:outline-none"
        >
          <span
            className="truncate font-medium"
            style={{ color: selected ? 'var(--accent-default)' : 'var(--content-strong)' }}
          >
            {categoryDisplayName(node)}
          </span>
          <span className="shrink-0 font-mono text-xs" style={{ color: 'var(--content-muted)' }}>
            {node.categoryCode}
          </span>
        </button>
      </div>

      {expanded && visibleChildren.length > 0 ? (
        <ul>
          {visibleChildren.map((child) => (
            <TreeRow
              key={child.categoryId}
              node={child}
              depth={depth + 1}
              keyword={keyword}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

export const CategorySearchSelect = ({
  value,
  disabled,
  errorMessage,
  onChange,
  placeholder = '카테고리를 선택하세요',
  testId = 'category-search-select',
  includeHidden = false,
}: CategorySearchSelectProps) => {
  const publicQuery = useCategoriesQuery({ enabled: !includeHidden });
  const adminQuery = useAdminCategoryItemsQuery({ enabled: includeHidden });
  const query = includeHidden ? adminQuery : publicQuery;
  const items = useMemo(() => query.data ?? [], [query.data]);

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [draft, setDraft] = useState<string | null>(value);

  useEffect(() => {
    if (open) {
      setDraft(value);
      setKeyword('');
    }
  }, [open, value]);

  const tree = useMemo(() => buildTree(items), [items]);
  const triggerPath = useMemo(() => buildPath(items, value), [items, value]);
  const triggerLabel = triggerPath.length > 0 ? triggerPath.join(' > ') : placeholder;

  const trimmedKeyword = keyword.trim().toLowerCase();
  const visibleRoots = useMemo(
    () =>
      trimmedKeyword
        ? tree.filter((node) => matchesKeyword(node, trimmedKeyword))
        : tree,
    [tree, trimmedKeyword],
  );

  const handleConfirm = useCallback(() => {
    onChange(draft);
    setOpen(false);
  }, [draft, onChange]);

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className="flex min-w-0 flex-1 items-center justify-between rounded-md border px-3 text-left text-sm disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            borderColor: 'var(--divider-strong)',
            background: 'var(--surface-0)',
            height: 'var(--density-compact-form-h)',
          }}
        >
          <span
            className="truncate"
            style={{ color: value ? 'var(--content-strong)' : 'var(--content-muted)' }}
          >
            {triggerLabel}
          </span>
          <span className="ml-2 shrink-0 text-xs" style={{ color: 'var(--accent-default)' }}>
            카테고리 선택
          </span>
        </button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={handleClear}
            data-testid={`${testId}-clear`}
          >
            해제
          </Button>
        ) : null}
      </div>

      {errorMessage ? (
        <span className="text-xs" style={{ color: 'var(--signal-danger)' }}>
          {errorMessage}
        </span>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>카테고리 선택</DialogTitle>
            <DialogDescription>대/중/소 카테고리에서 하나를 선택하세요.</DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex flex-col gap-3">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--content-muted)' }}
              />
              <Input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="카테고리명 또는 코드 검색"
                className="pl-8"
                aria-label="카테고리 검색"
                data-testid={`${testId}-search`}
              />
            </div>

            <div
              className="rounded-md border"
              style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
            >
              <ul role="tree" className="max-h-[360px] overflow-y-auto p-1">
                {query.isPending ? (
                  <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--content-muted)' }}>
                    로딩 중...
                  </li>
                ) : query.isError ? (
                  <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--signal-danger)' }}>
                    카테고리 목록을 불러오지 못했습니다
                  </li>
                ) : visibleRoots.length === 0 ? (
                  <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--content-muted)' }}>
                    {keyword ? '검색 결과 없음' : '카테고리가 없습니다'}
                  </li>
                ) : (
                  visibleRoots.map((node) => (
                    <TreeRow
                      key={node.categoryId}
                      node={node}
                      depth={0}
                      keyword={trimmedKeyword}
                      selectedId={draft}
                      onSelect={setDraft}
                    />
                  ))
                )}
              </ul>
            </div>

            <div
              className="rounded-md px-3 py-2 text-xs"
              style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
            >
              {draft ? (
                <span>
                  선택:{' '}
                  <span style={{ color: 'var(--content-strong)' }}>
                    {buildPath(items, draft).join(' > ')}
                  </span>
                </span>
              ) : (
                '선택된 카테고리 없음'
              )}
            </div>

            <div
              className="flex justify-end gap-2 border-t pt-3"
              style={{ borderColor: 'var(--divider-strong)' }}
            >
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                취소
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!draft}
                onClick={handleConfirm}
                data-testid={`${testId}-confirm`}
              >
                확인
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
